import { useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import { Button } from "@/shared/components/ui/button";
import { SafeText } from "@/shared/components/SafeText";
import { AccessErrorAlert } from "./AccessErrorAlert";
import { LocationPicker } from "./LocationPicker";
import { ORGANIZATION_LOCATIONS_MAX } from "./location-limits";
import type { AssignableRole } from "./access-client";
import { useGoneLocations, withoutIds } from "./reconcile-locations";
import { useChangeMemberRole } from "./use-access-mutations";
import { useAccessSubmit } from "./use-access-submit";

interface ChangeMemberRoleDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly organizationId: string;
  readonly membershipId: string;
  readonly memberName: string;
  /** OWNER rows have no role change, so only the two assignable roles are accepted. */
  readonly currentRole: AssignableRole;
  /**
   * The viewer also holds `org.members.manage`. Moving an ADMIN to STAFF picks
   * locations through an endpoint that needs it, so without it that direction
   * is explained instead of offered. STAFF -> ADMIN needs no locations.
   */
  readonly canAssignLocations: boolean;
  /** Fired after the API accepted the change, with the new role, right before closing. */
  readonly onDone: (newRole: AssignableRole) => void;
  /**
   * The membership was not in the state this dialog assumed (changed by someone
   * else, no longer active...). The reads were already refreshed; the dialog
   * closes and hands the reason over, so the host shows it next to fresh data.
   */
  readonly onStale: (message: string) => void;
}

/**
 * Moves one member between ADMIN and STAFF in place (same membership row).
 * To STAFF at least one location of the organization is required; to ADMIN
 * the assignments are cleared, which the dialog warns about before confirming.
 */
export function ChangeMemberRoleDialog(props: ChangeMemberRoleDialogProps) {
  // Mounted only while open (and per membership): the picked locations and
  // the last error start clean every time without an effect to reset them.
  if (!props.open) return null;
  return <ChangeMemberRoleDialogBody key={props.membershipId} {...props} />;
}

function ChangeMemberRoleDialogBody({
  open,
  onOpenChange,
  organizationId,
  membershipId,
  memberName,
  currentRole,
  canAssignLocations,
  onDone,
  onStale,
}: ChangeMemberRoleDialogProps) {
  const { t } = useTranslation();
  const change = useChangeMemberRole();
  // Frozen at open: the move the admin confirms. If a concurrent change flips the
  // row's role while the dialog stays open (a retryable refusal), a retry must not
  // silently become the opposite move; the API refuses it if it no longer applies.
  const [startRole] = useState(currentRole);
  const target: AssignableRole = startRole === "ADMIN" ? "STAFF" : "ADMIN";
  const needsLocations = target === "STAFF";
  const missingMembersPermission = needsLocations && !canAssignLocations;
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const goneLocations = useGoneLocations(organizationId);

  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const formValid =
    !missingMembersPermission &&
    (!needsLocations || (selected.size > 0 && selected.size <= ORGANIZATION_LOCATIONS_MAX));

  const { submit, error, handleOpenChange } = useAccessSubmit({
    run: () => {
      goneLocations.snapshot();
      return change.mutateAsync({
        organizationId,
        membershipId,
        role: target,
        ...(needsLocations ? { locationIds: [...selected] } : {}),
      });
    },
    isPending: change.isPending,
    canSubmit: formValid,
    onSuccess: () => {
      onDone(target);
      onOpenChange(false);
    },
    onStale,
    onOpenChange,
    onLocationGone: async () => {
      const gone = await goneLocations.find();
      setSelected((prev) => withoutIds(prev, gone));
    },
  });

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        data-testid="change-role-dialog"
        className="max-w-[calc(100vw-2rem)] sm:max-w-lg"
      >
        <DialogHeader>
          <DialogTitle>{t(`organizationAccess.changeRole.title.${target}`)}</DialogTitle>
          <DialogDescription>
            <SafeText>{memberName}</SafeText>
            {` · ${t(`organizationAccess.roles.${startRole}`)} → ${t(`organizationAccess.roles.${target}`)}`}
          </DialogDescription>
        </DialogHeader>

        <div className="min-w-0 space-y-4">
          {target === "ADMIN" ? (
            <div
              data-testid="change-role-warning"
              className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900"
            >
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
              <p>{t("organizationAccess.changeRole.toAdminWarning")}</p>
            </div>
          ) : missingMembersPermission ? (
            <p
              data-testid="change-role-needs-members-permission"
              className="text-sm text-destructive"
            >
              {t("organizationAccess.changeRole.needsMembersPermission")}
            </p>
          ) : (
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">
                {t("organizationAccess.changeRole.toStaffHint")}
              </p>
              {startRole === "ADMIN" && (
                <p
                  data-testid="change-role-invitations-note"
                  className="text-sm text-muted-foreground"
                >
                  {t("organizationAccess.changeRole.toStaffInvitationsNote")}
                </p>
              )}
              <LocationPicker
                organizationId={organizationId}
                selected={selected}
                onToggle={toggle}
                disabled={change.isPending}
                testIdPrefix="change-role"
              />
            </div>
          )}

          {!missingMembersPermission && (
            <p data-testid="change-role-notify-note" className="text-xs text-muted-foreground">
              {t("organizationAccess.changeRole.notifyNote")}
            </p>
          )}

          {error && <AccessErrorAlert testId="change-role-error" message={error} />}
        </div>

        <DialogFooter>
          <Button
            data-testid="change-role-cancel"
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={change.isPending}
          >
            {t("organizationAccess.cancel")}
          </Button>
          <Button
            data-testid="change-role-submit"
            onClick={() => void submit()}
            disabled={!formValid || change.isPending}
          >
            {change.isPending
              ? t("organizationAccess.changeRole.pending")
              : t("organizationAccess.changeRole.submit")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
